import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { filter, switchMap } from 'rxjs/operators';
import {
  DriverPetition,
  DriverPetitionComponent,
} from 'src/app/shared/components/vacations/driver-petition/driver-petition.component';
import { VacationSwitchComponent } from 'src/app/shared/components/vacations/vacation-switch/vacation-switch.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import {
  DriverResponse,
  DriverRequestsResponse,
  OperativaGroupDriversResponse,
  SimplifiedVacationRequest,
  CreateHolidayRequestPayload,
   UnavailabilityResponse,
  UnavailabilityHoliday,
} from 'src/app/shared/interfaces/holidays.interface';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { VacationDriverRequestDialogComponent } from 'src/app/shared/components/vacations/vacation-driver-request-dialog/vacation-driver-request-dialog.component';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { environment } from 'src/environments/environment';
import { DriverPetitionsTableComponent } from 'src/app/shared/components/vacations/driver-petitions-table/driver-petitions-table.component';
import { YearCalendarComponent } from 'src/app/shared/components/year-calendar/year-calendar.component';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  standalone: true,
  selector: 'app-driver-profile',
  imports: [
    LoaderComponent,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTabGroup,
    MatTab,
    // VacationSwitchComponent,
    DriverPetitionComponent,
    DriverPetitionsTableComponent,
    CalendarComponent,
    MatDivider,
    QeqPipe,
    YearCalendarComponent,
  ],
  templateUrl: './driver-profile.component.html',
  styleUrls: [
    './driver-profile.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class DriverProfileComponent implements OnInit {
  driver: DriverResponse | null = null;
  operativaGroup: string = '';
  operativaName: string = '';
  protocolName: string = '';
  pendingPetitions: DriverPetition[] = [];
  previousPetitions: DriverPetition[] = [];
  previousRequests: SimplifiedVacationRequest[] = [];
  calendarEvents: any[] = [];
  vacationEvents: any[] = [];
  unavailabilityEvents: any[] = [];
  year?: number;
  opId?: number;
  id?: number;
  driverId?: number;
  imageLoadError: boolean = false;
  employeeImageUrl: string = '';
  operativaGroupId?: number;
  monthlyView: boolean = false;
  yearlyView: boolean = true;

  constructor(
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private holidaysService: HolidaysService,
    private location: Location,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  goBack(): void {
    this.location.back();
  }

  getEmployeeImageUrl(personnelnumber: string): string {
    // console.log(`${environment.conductorsAzureBlobURL}${personnelnumber}.jpg`);
    return `${environment.conductorsAzureBlobURL}${personnelnumber}.jpg`;
  }

  onImageError(event: any) {
    this.imageLoadError = true;
    event.target.src = 'assets/images/profile-default.jpg';
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.params['id']);
    this.year = this.route.snapshot.params['year'];
    this.opId = Number(this.route.snapshot.params['opId']);
    this.operativaGroupId = this.opId; // opId now represents operativa_group_id
    this.driverId = Number(this.route.snapshot.params['driverId']);

    if (this.id && this.operativaGroupId && this.driverId) {
      this.loadDriverProfile();
      this.loadDriverRequests();
      this.loadUnavailabilities();

    }
  }

  loadDriverProfile(): void {
    if (!this.id || !this.operativaGroupId || !this.driverId) {
      console.error('Missing required parameters');
      return;
    }

    this.loaderService.showLoader();
    this.holidaysService
      .getOperativaGroupDriver({
        exercise_id: this.id,
        operativa_group_id: this.operativaGroupId,
        driver_id: this.driverId,
      })
      .subscribe({
        next: (data: OperativaGroupDriversResponse) => {
          this.loaderService.hideLoader();

          // Extract first driver from the drivers array
          this.driver = data.drivers[0] || null;

          this.operativaGroup = this.driver?.operativa_group?.name || '';

          // Extract operativa name from driver's operativa (not operativa_base or operativa_group)
          this.operativaName =
            this.driver?.operativa?.name || data.operativa_group_name;

          // Extract protocol name from driver's protocol
          this.protocolName = this.driver?.protocol?.name || '';

          this.employeeImageUrl = `${environment.conductorsAzureBlobURL}${
            this.driver?.employee?.first_name || ''
          }.jpg`;

          // console.log('Driver data:', this.driver);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error fetching driver data:', error);
        },
      });
  }

  loadData(): void {
    this.loadDriverProfile();
    this.loadDriverRequests();
    this.loadUnavailabilities();
  }

  loadDriverRequests(): void {
    if (!this.id || !this.operativaGroupId || !this.driverId) {
      console.error('Missing required parameters');
      return;
    }

    this.loaderService.showLoader();

    this.holidaysService
      .getOperativaGroupDriverRequests({
        exercise_id: this.id,
        operativa_group_id: this.operativaGroupId,
        driver_id: this.driverId,
      })
      .subscribe({
        next: (data: DriverRequestsResponse) => {
          // console.log('Driver requests data:', data);

          // Transform VacationRequest to DriverPetition format
          const allPetitions = data.requests.map((request) =>
            this.transformRequestToPetition(request)
          );

          // Separate pending and non-pending requests
          this.pendingPetitions = allPetitions.filter(
            (petition, index) => data.requests[index].status === 'pending'
          );
          this.previousPetitions = allPetitions.filter(
            (petition, index) => data.requests[index].status !== 'pending'
          );

          // Store raw previous requests for the table
          this.previousRequests = data.requests.filter(
            (request) => request.status !== 'pending'
          );

          // Process calendar events
          this.processCalendarEvents(data.requests);
          this.loaderService.hideLoader();
          // console.log('Pending petitions:', this.pendingPetitions);
          // console.log('Previous petitions:', this.previousPetitions);
          // console.log('Previous requests:', this.previousRequests);
          // console.log('Calendar events:', this.calendarEvents);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error fetching driver requests:', error);
        },
      });
  }

  private processCalendarEvents(requests: SimplifiedVacationRequest[]): void {
    this.vacationEvents = [];

    // Only process approved and pending requests
    const relevantRequests = requests.filter(
      (request) => request.status === 'approved' || request.status === 'pending'
    );

    relevantRequests.forEach((request) => {
      // Use exact dates from weeks if available, otherwise generate range
      let dates: string[];

      if (request.weeks && request.weeks.length > 0) {
        // Extract exact dates from weeks and convert DD/MM/YYYY to YYYY-MM-DD
        dates = request.weeks.flatMap((week) =>
          week.dates.map((dateStr) => this.convertDDMMYYYYtoISO(dateStr))
        );
      } else {
        // Fallback: generate date range from start to end date
        dates = this.generateDateRange(request.start_date, request.end_date);
      }

      dates.forEach((date) => {
        // Determine the type for calendar styling
        let eventType = '';

        if (request.status === 'pending') {
          eventType = 'pending-vacation';
        } else if (request.status === 'approved') {
          if (request.request_type === 'personal_day') {
            eventType = 'approved-personal-vacation';
          } else {
            eventType = 'approved-vacation';
          }
        }

        this.vacationEvents.push({
          date: date, // Format: YYYY-MM-DD
          type: eventType,
          requestId: request.id,
          requestType: request.request_type,
          status: request.status,
        });
      });
    });
  }

  private readonly UNAVAILABILITY_TYPES: Record<string, { label: string; css: string }> = {
    ac:  { label: 'Ausencia compensar',         css: 'unavailability-ac' },
    ar:  { label: 'Ausencia retribuida',        css: 'unavailability-ar' },
    ba:  { label: 'Baja accidente',             css: 'unavailability-ba' },
    bm:  { label: 'Baja enfermedad',            css: 'unavailability-bm' },
    bmp: { label: 'Baja maternidad/paternidad', css: 'unavailability-bmp' },
    e:   { label: 'Excedencia',                 css: 'unavailability-e' },
    f:   { label: 'Festivo',                    css: 'unavailability-f' },
    fr:  { label: 'Festivo',                    css: 'unavailability-f' },
  };

  loadUnavailabilities(): void {
    const driver_id = this.driverId;
    if (!driver_id) {
      return;
    }
    this.loaderService.showLoader();
    this.holidaysService.UnavailabilityEmployee(driver_id).subscribe({
      next: (res: UnavailabilityResponse) => {
        this.loaderService.hideLoader();
        this.unavailabilityEvents = this.processUnavailabilities(
          res.results?.data?.holidays ?? []
        );
        this.rebuildCalendar();
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al cargar las indisponibilidades');
      },
    });
  }

  private processUnavailabilities(holidays: UnavailabilityHoliday[]): any[] {
    return holidays.flatMap((h) => {
      const info = this.UNAVAILABILITY_TYPES[h.type] ?? {
        label: h.type,
        css: 'unavailability-absence',
      };
      return this.getDatesBetween(new Date(h.startDate), new Date(h.endDate)).map(
        (date) => ({
          date,
          type: info.css,
          label: info.label,
          unavailabilityId: h.id,
          unavailabilityType: h.type,
          status: 'unavailable',
        })
      );
    });
  }

  private rebuildCalendar(): void {
    // Indisponibilidades primero, vacaciones después (nueva referencia para detectar el cambio)
    this.calendarEvents = [...this.unavailabilityEvents, ...this.vacationEvents];
  }

  /**
   * Convert DD/MM/YYYY to YYYY-MM-DD format
   */
  private convertDDMMYYYYtoISO(dateStr: string): string {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  }

  private transformRequestToPetition(
    request: SimplifiedVacationRequest
  ): DriverPetition {
    // SimplifiedVacationRequest already has formatted dates (DD/MM/YYYY)
    // and calculated duration, so we just need to transform the data

    // Generate date array in YYYY-MM-DD format for the calendar
    const dates = this.generateDateRange(request.start_date, request.end_date);

    // Transform weeks if available
    const weeks = request.weeks?.map((week) => ({
      weekNumber: week.week_number,
      year: week.year,
      weekDisplay: week.week_display,
      dayCount: week.day_count,
      dates: week.dates,
      startDate: week.start_date,
      endDate: week.end_date,
    }));

    return {
      id: request.id,
      uuid: request.uuid,
      startDate: request.start_date, // Already in DD/MM/YYYY format
      endDate: request.end_date, // Already in DD/MM/YYYY format
      days: request.duration_days,
      personal: request.request_type === 'personal_day',
      dates: dates, // Pass the generated dates in YYYY-MM-DD format
      weeks: weeks, // Pass the weeks breakdown
    };
  }

  private formatDate(dateStr: string): string {
    // Convert YYYY-MM-DD to DD/MM/YYYY
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  /**
   * Generate date range from start to end date
   * @param startDateStr Start date in DD/MM/YYYY format
   * @param endDateStr End date in DD/MM/YYYY format
   * @returns Array of dates in YYYY-MM-DD format
   */
  private generateDateRange(
    startDateStr: string,
    endDateStr: string
  ): string[] {
    if (!startDateStr || !endDateStr) return [];

    // Parse DD/MM/YYYY to Date objects
    const parseDate = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    };

    // Format Date to YYYY-MM-DD
    const formatToISO = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDate = parseDate(startDateStr);
    const endDate = parseDate(endDateStr);
    const dates: string[] = [];

    // Generate all dates from start to end (inclusive)
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(formatToISO(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  get isLoading(): boolean {
    return this.loaderService.getLoader();
  }

  onGenerateRequest(): void {

    if (!this.driverId) {
      return;
    }

    const driverId = this.driverId;

    const dialogRef = this.dialog.open(VacationDriverRequestDialogComponent, {
      width: '700px'
    });

    dialogRef.afterClosed()
    .pipe(
      filter(result => !!result),
      switchMap(result => {
        const payload: CreateHolidayRequestPayload = {
          driver_assignment: driverId, // Si driverId contiene el ID de DriverAssignment
          dates: this.getDatesBetween(result.startDate, result.endDate),
          request_type: result.type
        };

        return this.holidaysService.createHolidayRequest(payload);
      })
    )
    .subscribe({
      next: () => {
        this.openSnackBar('Solicitud creada correctamente.', 'Cerrar');
        this.loadData();
      },
      error: err => {
        console.error(err);
        this.openSnackBar('Error al crear la solicitud.', 'Cerrar');
      }
    });
  }

  private getDatesBetween(start: Date, end: Date): string[] {

    const dates: string[] = [];
    const current = new Date(start);

    while (current <= end) {
      dates.push(this.formatDates(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  private formatDates(date: Date): string {

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private openSnackBar(message: string, action: string = 'Cerrar'): void {
    this.snackBar.open(message, action, {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
  unavailabilityLegend = [
  { label: 'Ausencia compensar',         color: '#8E24AA' },
  { label: 'Ausencia retribuida',        color: '#29B6F6' },
  { label: 'Baja accidente',             color: '#D32F2F' },
  { label: 'Baja enfermedad',            color: '#EC407A' },
  { label: 'Baja maternidad/paternidad', color: '#5C6BC0' },
  { label: 'Excedencia',                 color: '#757575' },
  { label: 'Festivo',                    color: '#90A4AE' },
];
}
